import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/oraya-api";

const CommandContext = createContext(null);
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

export function CommandProvider({ children }) {
  const [state, setState] = useState({ loading: true, boot: null, error: null });
  const wsRef = useRef(null);
  const listeners = useRef(new Set());
  const [connected, setConnected] = useState(false);

  const loadBoot = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/cc/bootstrap");
        setState({ loading: false, boot: data, error: null });
        return data;
      } catch (e) {
        const status = e.response?.status;
        if (status === 403) { setState({ loading: false, boot: null, error: "forbidden" }); return null; }
        // transient 401 (cookie just set) -> brief retry before giving up
        if (attempt < 2) { await new Promise((r) => setTimeout(r, 500)); continue; }
        setState({ loading: false, boot: null, error: "unauth" });
        return null;
      }
    }
  }, []);

  useEffect(() => { loadBoot(); }, [loadBoot]);

  // Realtime: fetch ws ticket then connect. Reconnect with backoff (polling fallback via listeners refetch).
  useEffect(() => {
    if (!state.boot) return;
    let stopped = false;
    let retry = 0;
    let pollTimer = null;

    const connect = async () => {
      try {
        const { data } = await api.get("/cc/ws-ticket");
        const ws = new WebSocket(`${WS_BASE}/api/ws?token=${data.ticket}`);
        wsRef.current = ws;
        ws.onopen = () => { setConnected(true); retry = 0; if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            listeners.current.forEach((fn) => fn(msg));
          } catch {}
        };
        ws.onclose = () => {
          setConnected(false);
          if (stopped) return;
          retry += 1;
          // polling fallback: notify listeners to refetch every 15s while disconnected
          if (!pollTimer) pollTimer = setInterval(() => listeners.current.forEach((fn) => fn({ type: "poll" })), 15000);
          setTimeout(connect, Math.min(1000 * retry, 8000));
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        if (!stopped) setTimeout(connect, 4000);
      }
    };
    connect();
    return () => { stopped = true; if (pollTimer) clearInterval(pollTimer); try { wsRef.current?.close(); } catch {} };
  }, [state.boot]);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const can = useCallback((module, action) => {
    const perms = state.boot?.permissions?.[module] || [];
    return perms.includes(action);
  }, [state.boot]);

  return (
    <CommandContext.Provider value={{ ...state, connected, subscribe, can, reloadBoot: loadBoot }}>
      {children}
    </CommandContext.Provider>
  );
}

export const useCommand = () => useContext(CommandContext);

// Subscribe to realtime events of given types -> run callback (also runs on 'poll' fallback).
export function useRealtime(types, cb) {
  const { subscribe } = useCommand();
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === "poll" || !types || types.includes(msg.type)) cbRef.current(msg);
    });
  }, [subscribe, types]);
}
